import { BackendCN54BisData, CN56NonSampledRow } from './types';

/**
 * Maps backend quarter names to table quarter names
 */
const mapQuarterToTableQuarter = (quarter: string): string => {
  switch (quarter) {
    case 'ONE': return 'Q1';
    case 'TWO': return 'Q2';
    case 'THREE': return 'Q3';
    case 'FOUR': return 'Q4';
    default: return quarter; // Return as-is for any other format like 'Total'
  }
};

/**
 * Transforms backend CN56 Non-sampled flows data to table format
 * Legacy function - combines format separated and mixed
 */
export function transformCN56NonSampledData(backendData: BackendCN54BisData[]): CN56NonSampledRow[] {
  const tableRows: CN56NonSampledRow[] = [];
  
  // Process each PPO's data
  backendData.forEach((ppoData, ppoIndex) => {
    const { formatSeparatedNonSampledFlow, mixedNonSampledFlow } = ppoData;
    
    // Check if we have any flow data available
    const hasFormatSeparated = formatSeparatedNonSampledFlow && !formatSeparatedNonSampledFlow.empty;
    const hasMixed = mixedNonSampledFlow && !mixedNonSampledFlow.empty;
    
    if (!hasFormatSeparated && !hasMixed) {
      return;
    }
    
    // Use formatSeparatedNonSampledFlow if available, otherwise use mixedNonSampledFlow
    const flowToUse = hasFormatSeparated ? formatSeparatedNonSampledFlow : mixedNonSampledFlow;
    
    if (!flowToUse) {
      return;
    }
    
    const rows = transformSingleNonSampledFlow(flowToUse, ppoIndex);
    tableRows.push(...rows);
  });
  
  return tableRows;
}

/**
 * Transforms a single non-sampled flow (format separated or mixed) to table format
 */
export function transformSingleNonSampledFlow(flow: any, ppoIndex: number): CN56NonSampledRow[] {
  const tableRows: CN56NonSampledRow[] = [];
  
  if (!flow || flow.empty) {
    return [];
  }
  
  // Always show all quarters (Q1, Q2, Q3, Q4) regardless of data availability
  const allQuarters = ['ONE', 'TWO', 'THREE', 'FOUR'];
  
  // Create rows for each quarter
  allQuarters.forEach((quarter: string, quarterIndex: number) => {
    const tableQuarter = mapQuarterToTableQuarter(quarter);
    
    // Get data for this quarter from flow
    const quarterData = getQuarterData(flow, quarter);
    
    const row: CN56NonSampledRow = {
      id: `${ppoIndex}_${quarterIndex}`,
      quarter: tableQuarter,
      ...quarterData
    };
    
    tableRows.push(row);
  });
  
  // Add Total row
  const totalData = getTotalData(flow);
  tableRows.push({
    id: `${ppoIndex}_total`,
    quarter: 'Total',
    ...totalData
  });
  
  return tableRows;
}

/**
 * Gets quarter data for a specific quarter - ONLY backend values, NO calculations
 */
function getQuarterData(flow: any, quarter: string) {
  const data = {
    a_actual_weight: undefined as number | undefined,
    a_number_of_items: undefined as number | undefined,
    d_actual_weight: undefined as number | undefined,
    d_number_of_items: undefined as number | undefined,
    b_actual_weight: undefined as number | undefined,
    b_number_of_items: undefined as number | undefined,
    c_actual_weight: undefined as number | undefined,
    c_number_of_items: undefined as number | undefined,
  };
  
  // Extract data directly from backend for each mail category
  flow.sections?.forEach((section: any) => {
    const mailCategory = section.mailCategory;
    
    // Find the specific quarter data from backend rows
    const quarterData = section.rows?.find((r: any) => r.quarter === quarter);
    
    if (quarterData && !quarterData.empty) {
      const category = mailCategory.toLowerCase();
      
      // Use formatted values if available, otherwise fall back to raw values
      if (category === 'a') {
        data.a_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null
          ? quarterData.actualWeightFormatted : quarterData.actualWeight;
        data.a_number_of_items = quarterData.numberOfItemsFormatted !== undefined && quarterData.numberOfItemsFormatted !== null
          ? quarterData.numberOfItemsFormatted : quarterData.numberOfItems;
      } else if (category === 'b') {
        data.b_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null
          ? quarterData.actualWeightFormatted : quarterData.actualWeight;
        data.b_number_of_items = quarterData.numberOfItemsFormatted !== undefined && quarterData.numberOfItemsFormatted !== null
          ? quarterData.numberOfItemsFormatted : quarterData.numberOfItems;
      } else if (category === 'c') {
        data.c_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null
          ? quarterData.actualWeightFormatted : quarterData.actualWeight;
        data.c_number_of_items = quarterData.numberOfItemsFormatted !== undefined && quarterData.numberOfItemsFormatted !== null
          ? quarterData.numberOfItemsFormatted : quarterData.numberOfItems;
      } else if (category === 'd') {
        data.d_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null
          ? quarterData.actualWeightFormatted : quarterData.actualWeight;
        data.d_number_of_items = quarterData.numberOfItemsFormatted !== undefined && quarterData.numberOfItemsFormatted !== null
          ? quarterData.numberOfItemsFormatted : quarterData.numberOfItems;
      }
    }
  });
  
  return data;
}

/**
 * Gets total data - ONLY backend totalRow values, NO calculations
 */
function getTotalData(flow: any) {
  const data = {
    a_actual_weight: undefined as number | undefined,
    a_number_of_items: undefined as number | undefined,
    d_actual_weight: undefined as number | undefined,
    d_number_of_items: undefined as number | undefined,
    b_actual_weight: undefined as number | undefined,
    b_number_of_items: undefined as number | undefined,
    c_actual_weight: undefined as number | undefined,
    c_number_of_items: undefined as number | undefined,
  };
  
  // ONLY use backend totalRow values - NO client-side calculations
  // Extract totals directly from backend totalRow for each section
  flow.sections?.forEach((section: any) => {
    const mailCategory = section.mailCategory?.toLowerCase();
    const totalRow = section.totalRow;
    
    if (totalRow && !totalRow.empty) {
      // Use formatted values if available, otherwise fall back to raw values
      if (mailCategory === 'a') {
        data.a_actual_weight = totalRow.actualWeightFormatted !== undefined && totalRow.actualWeightFormatted !== null
          ? totalRow.actualWeightFormatted : totalRow.actualWeight;
        data.a_number_of_items = totalRow.numberOfItemsFormatted !== undefined && totalRow.numberOfItemsFormatted !== null
          ? totalRow.numberOfItemsFormatted : totalRow.numberOfItems;
      } else if (mailCategory === 'b') {
        data.b_actual_weight = totalRow.actualWeightFormatted !== undefined && totalRow.actualWeightFormatted !== null
          ? totalRow.actualWeightFormatted : totalRow.actualWeight;
        data.b_number_of_items = totalRow.numberOfItemsFormatted !== undefined && totalRow.numberOfItemsFormatted !== null
          ? totalRow.numberOfItemsFormatted : totalRow.numberOfItems;
      } else if (mailCategory === 'c') {
        data.c_actual_weight = totalRow.actualWeightFormatted !== undefined && totalRow.actualWeightFormatted !== null
          ? totalRow.actualWeightFormatted : totalRow.actualWeight;
        data.c_number_of_items = totalRow.numberOfItemsFormatted !== undefined && totalRow.numberOfItemsFormatted !== null
          ? totalRow.numberOfItemsFormatted : totalRow.numberOfItems;
      } else if (mailCategory === 'd') {
        data.d_actual_weight = totalRow.actualWeightFormatted !== undefined && totalRow.actualWeightFormatted !== null
          ? totalRow.actualWeightFormatted : totalRow.actualWeight;
        data.d_number_of_items = totalRow.numberOfItemsFormatted !== undefined && totalRow.numberOfItemsFormatted !== null
          ? totalRow.numberOfItemsFormatted : totalRow.numberOfItems;
      }
    }
  });
  
  // Fallback: use grandTotalRow only if no section data exists
  if (flow.grandTotalRow && !flow.grandTotalRow.empty) {
    const grandTotal = flow.grandTotalRow;
    
    // Use grand totals only if no category-specific data was found
    if (data.a_actual_weight === undefined && data.b_actual_weight === undefined && 
        data.c_actual_weight === undefined && data.d_actual_weight === undefined) {
      // For non-sampled data, assign grand total to category A as default
      // Use formatted values if available
      data.a_actual_weight = grandTotal.actualWeightFormatted !== undefined && grandTotal.actualWeightFormatted !== null
        ? grandTotal.actualWeightFormatted : grandTotal.actualWeight;
      data.a_number_of_items = grandTotal.numberOfItemsFormatted !== undefined && grandTotal.numberOfItemsFormatted !== null
        ? grandTotal.numberOfItemsFormatted : grandTotal.numberOfItems;
    }
  }
  
  return data;
}
